import type { ApiSecurityReport } from '../types';
import { StatusBadge } from './StatusBadge';
import { RiskLevelTag } from './RiskLevelTag';

interface Props {
  reports: ApiSecurityReport[];
  loading?: boolean;
  error?: string | null;
  onRowClick?: (report: ApiSecurityReport) => void;
}

export function SecurityReportsTable({
  reports,
  loading = false,
  error = null,
  onRowClick
}: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white/80 px-4 py-10 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
        <span className="ml-3">Loading security reports…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/40 bg-red-50/80 px-4 py-3 text-sm text-red-900 shadow-sm dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">
        <div className="font-semibold">Failed to load reports</div>
        <div className="mt-1 opacity-90">{error}</div>
      </div>
    );
  }

  if (!reports.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-6 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
        No security reports match the current filters.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700/70 dark:bg-slate-900/70">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700/80">
        <thead className="bg-slate-50/90 text-slate-500 dark:bg-slate-900/90 dark:text-slate-400">
          <tr>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Package
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Version
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Status
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Risk score
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Risk level
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Last scan
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide">
              Summary
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {reports.map((report) => {
            const lastScan = new Date(report.lastScanAt);
            const lastScanLabel = Number.isNaN(lastScan.getTime())
              ? report.lastScanAt
              : lastScan.toLocaleString();

            return (
              <tr
                key={`${report.packageName}@${report.version}`}
                className="cursor-pointer bg-white/80 text-xs text-slate-800 transition hover:bg-slate-50 dark:bg-slate-900/60 dark:text-slate-200 dark:hover:bg-slate-800/70"
                onClick={onRowClick ? () => onRowClick(report) : undefined}
              >
                <td className="whitespace-nowrap px-4 py-2.5 text-sm font-mono text-emerald-700 dark:text-emerald-200">
                  {report.packageName}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 font-mono text-slate-700 dark:text-slate-200">
                  {report.version}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5">
                  <StatusBadge status={report.status} />
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-800 dark:text-slate-200">
                  {report.riskScore.toFixed(0)}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5">
                  <RiskLevelTag level={report.riskLevel} />
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-600 dark:text-slate-300">
                  {lastScanLabel}
                </td>
                <td className="max-w-xl px-4 py-2.5 text-slate-700 dark:text-slate-200">
                  <div className="line-clamp-2 leading-relaxed">{report.summary}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
